import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { environment } from '../../../../environment/environment';
import { BlacklistedMobileNumberItem } from '../hooks/useBlacklistedMobileNumbers';
import { Drawer } from '../../../../components/drawers';
import toast from '../../../../utils/toast';
import BlacklistedMobileNumberFormFields from './BlacklistedMobileNumberFormFields';

interface EditBlacklistedMobileNumberFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (updatedItem: BlacklistedMobileNumberItem) => void;
  blacklistedMobileNumber: BlacklistedMobileNumberItem | null;
}

const EditBlacklistedMobileNumberForm: React.FC<EditBlacklistedMobileNumberFormProps> = ({
  isOpen,
  onClose,
  onSuccess,
  blacklistedMobileNumber
}) => {
  const [mobileNumber, setMobileNumber] = useState('');
  const [reason, setReason] = useState('');
  const [active, setActive] = useState(true);
  const [loading, setLoading] = useState(false);
  const [isPhoneValid, setIsPhoneValid] = useState(true);

  const handleClose = () => {
    setMobileNumber(blacklistedMobileNumber?.mobile_number || '');
    setReason(blacklistedMobileNumber?.reason || '');
    setActive(blacklistedMobileNumber?.active !== undefined ? blacklistedMobileNumber.active : true);
    setIsPhoneValid(true);
    onClose();
  };

  const handlePhoneValidationChange = (isValid: boolean) => {
    setIsPhoneValid(isValid);
  };

  useEffect(() => {
    if (blacklistedMobileNumber) {
      setMobileNumber(blacklistedMobileNumber.mobile_number || '');
      setReason(blacklistedMobileNumber.reason || '');
      setActive(blacklistedMobileNumber.active !== undefined ? blacklistedMobileNumber.active : true);
      setIsPhoneValid(true);
    }
  }, [blacklistedMobileNumber]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!blacklistedMobileNumber) return;

    setLoading(true);

    try {
      const payload = {
        mobile_number: mobileNumber,
        reason,
        active
      };
      const response = await axios.put<BlacklistedMobileNumberItem>(`${environment.baseUrl}/blacklisted-mobile-numbers/${blacklistedMobileNumber.id}/update/`, payload);
      toast.success('Blacklisted mobile number updated successfully');
      onSuccess(response.data);
      handleClose();
    } catch (err: any) {
      if (err.response?.status === 409) {
        toast.error('This mobile number has already been blacklisted');
      } else if (err.response?.status === 400) {
        toast.error('Invalid data provided');
      } else {
        toast.error(err.response?.data?.message || 'Failed to update blacklisted mobile number');
      }
    } finally {
      setLoading(false);
    }
  };

  const isValid = mobileNumber.trim().length > 0 && isPhoneValid;

  return (
    <Drawer isOpen={isOpen} onClose={handleClose}>
      <Drawer.Header title="Edit Blacklisted Mobile Number" onClose={handleClose} />

      <Drawer.Content>
        <form onSubmit={handleSubmit}>
          <BlacklistedMobileNumberFormFields
            mobileNumber={mobileNumber}
            reason={reason}
            active={active}
            onMobileNumberChange={setMobileNumber}
            onReasonChange={setReason}
            onActiveChange={setActive}
            onPhoneValidationChange={handlePhoneValidationChange}
          />
        </form>
      </Drawer.Content>

      <Drawer.Footer fullWidth>
        <Drawer.PrimaryButton
          type="submit"
          onClick={handleSubmit}
          disabled={!isValid || loading}
        >
          {loading ? 'Updating...' : 'Update'}
        </Drawer.PrimaryButton>
      </Drawer.Footer>
    </Drawer>
  );
};

export default EditBlacklistedMobileNumberForm;